import React from "react";
import { Platform, View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { FontFamily } from "../constants/fonts";
import { useTheme } from "../theme";

interface LhActionPlanButtonGroupProps {
  status: "incomplete" | "completed";
  onStatusChange?: (newStatus: "incomplete" | "completed") => void;
  disabled?: boolean;
}

const LhActionPlanButtonGroup: React.FC<LhActionPlanButtonGroupProps> = ({
  status,
  onStatusChange,
  disabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark" && Platform.OS !== "web";

  const handlePress = (newStatus: "incomplete" | "completed") => {
    if (!disabled && onStatusChange && newStatus !== status) {
      onStatusChange(newStatus);
    }
  };

  return (
    <View style={styles.container}>
      <View style={[styles.buttonGroup, isDark && { borderColor: colors.border.default, backgroundColor: colors.border.default }]}>
        <TouchableOpacity
          style={[
            styles.button,
            styles.leftButton,
            isDark && status !== "incomplete" && { backgroundColor: colors.background.elevated },
            status === "incomplete" && styles.activeButton,
            disabled && styles.disabledButton,
          ]}
          onPress={() => handlePress("incomplete")}
          disabled={disabled}
          activeOpacity={0.7}
        >
          <Text
            style={[
              styles.buttonText,
              isDark && status !== "incomplete" && { color: colors.text.secondary },
              status === "incomplete" && styles.activeButtonText,
              disabled && styles.disabledText,
            ]}
          >
            Incomplete
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.button,
            styles.rightButton,
            isDark && status !== "completed" && { backgroundColor: colors.background.elevated },
            status === "completed" && styles.activeButton,
            disabled && styles.disabledButton,
          ]}
          onPress={() => handlePress("completed")}
          disabled={disabled}
          activeOpacity={0.7}
        >
          <Text
            style={[
              styles.buttonText,
              isDark && status !== "completed" && { color: colors.text.secondary },
              status === "completed" && styles.activeButtonText,
              disabled && styles.disabledText,
            ]}
          >
            Completed
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "flex-start",
    justifyContent: "flex-start",
    paddingVertical: 4,
    minWidth: 180, // Ensure minimum width to prevent cropping
    width: "100%",
  },
  buttonGroup: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
    backgroundColor: "#D7E5F8",
    minWidth: 180, // Ensure minimum width for the button group
  },
  button: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: "#FBFCFE",
    justifyContent: "center",
    alignItems: "center",
    minWidth: 88,
    height: 32,
  },
  leftButton: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  rightButton: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    marginLeft: 1,
  },
  activeButton: {
    backgroundColor: "#005CBC",
  },
  disabledButton: {
    opacity: 0.5,
  },
  buttonText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 20,
    color: "#005CBC",
    textAlign: "center",
  },
  activeButtonText: {
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  disabledText: {
    color: "#999999",
  },
});

export default LhActionPlanButtonGroup;
